import type {
  FieldDef,
  FormSchema,
  QuestionDef,
} from "../risikoanalyseStep/types";

/**
 * Schema for the Cyber / IT / Datenschutz risk step form.
 *
 * Mirrors `for-ai/cyber.json`. Yes/no questions reveal an optional
 * "Kurzbeschreibung" textarea on `ja`.
 */

const yn = (
  question: string,
  key: string,
  followUpKey?: string,
  hint?: string,
  followUpLabel: string = "Kurzbeschreibung",
  followUpType: "textarea" | "text" = "textarea",
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  ...(followUpKey
    ? {
        showFollowUpWhen: { key, equals: "ja" },
        followUpFields: [
          {
            key: followUpKey,
            label: followUpLabel,
            type: followUpType,
          },
        ] as FieldDef[],
      }
    : {}),
});

const q = (question: string, field: FieldDef, hint?: string): QuestionDef => ({
  question,
  hint,
  field,
});

export const cyberSchema: FormSchema = {
  sections: [
    {
      key: "allgemein",
      title: "Allgemeine Fragen",
      color: "blue",
      subSteps: [
        {
          key: "allgemein",
          title: "Allgemeine Fragen",
          description:
            "Grundlegende Fragen zur aktuellen Cyber-Risikolage des Unternehmens.",
          questions: [
            yn(
              "Gibt es einen Schutz gegen Zerstörung, Beschädigung, Abhandenkommen von Sachen und Daten bei Angriffen?",
              "q1_schutz_angriffe",
              "q1_beschreibung",
              "Inkl. Schutz gegen Identitätsdiebstahl/Reputationsverlust sowie Schutz von Konten, Kredit- u. Zahlkarten bei Cyberangriffen.",
            ),
            yn(
              "Waren Sie schon mal von einem Cyberangriff betroffen und welche Auswirkungen hatte dies?",
              "q2_vorherige_angriffe",
              "q2_beschreibung",
            ),
            yn(
              "Sind Sie allen gesetzlichen Regelungen nachgekommen (DSGVO, EU-Richtlinien, …)?",
              "q3_gesetzliche_regelungen",
              "q3_beschreibung",
            ),
            yn(
              "Sind Teile Ihrer Produktionsmaschinen in einem Netzwerk / Internet eingebunden?",
              "q4_produktionsmaschinen_netzwerk",
              "q4_beschreibung",
            ),
          ],
        },
      ],
    },
    {
      key: "bausteine",
      title: "Bausteine der Cyberversicherung",
      color: "purple",
      subSteps: [
        {
          key: "bausteine",
          title: "Bausteine der Cyberversicherung",
          description:
            "Wählen Sie die gewünschten Versicherungsbausteine aus (Mehrfachauswahl möglich).",
          questions: [
            {
              question: "Welche Bausteine sollen abgesichert werden?",
              field: {
                key: "bausteine_auswahl",
                type: "iconCards",
                options: [
                  {
                    value: "hardware_eigenschaden",
                    label: "Hardware – Eigenschaden",
                    description:
                      "Reparatur oder Ersatz beschädigter Geräte nach einem Cyberangriff (z. B. Malware oder physische Manipulation).",
                    icon: "hardware",
                    color: "#E8A020",
                  },
                  {
                    value: "betriebsunterbrechung_eigenschaden",
                    label: "Betriebsunterbrechung – Eigenschaden",
                    description:
                      "Ersatz des entgangenen Gewinns und fortlaufender Kosten während der Ausfallzeit (Produktionsstillstand, IT-Ausfall).",
                    icon: "betrieb",
                    color: "#E05020",
                  },
                  {
                    value: "wiederherstellung_software_eigenschaden",
                    label: "Wiederherstellung + Software – Eigenschaden",
                    description:
                      "Wiederherstellung / Neuinstallation von Programmen und Betriebssystemen sowie Lizenzkosten nach Datenverlust.",
                    icon: "software",
                    color: "#8B6DB0",
                  },
                  {
                    value: "haftpflicht_fremdschaeden",
                    label: "Haftpflicht- / Fremdschäden",
                    description:
                      "Schutz vor Ansprüchen Dritter, wenn deren Daten oder Systeme durch einen Vorfall bei Ihnen beschädigt werden.",
                    icon: "haftpflicht",
                    color: "#4A90A4",
                  },
                  {
                    value: "datenschutzverletzung",
                    label: "Datenschutzverletzung",
                    description:
                      "Kosten für DSGVO-Meldungen, Benachrichtigungen, Rechtsberatung, PR-Maßnahmen und mögliche Bußgelder.",
                    icon: "datenschutz",
                    color: "#5A8A5A",
                  },
                ],
              },
            },
          ],
        },
      ],
    },
    {
      key: "it_daten",
      title: "IT & Daten",
      color: "green",
      subSteps: [
        {
          key: "it_daten",
          title: "IT & Daten",
          description: "Fragen zur IT-Betreuung und Art der gespeicherten Daten.",
          questions: [
            yn(
              "Werden Sie durch ein IT-Unternehmen betreut? (Ansprechpartner?)",
              "q5_it_unternehmen",
              "q5_beschreibung",
              undefined,
              "Name / Ansprechpartner des IT-Unternehmens",
              "text",
            ),
            yn(
              "Arbeiten Sie viel mit Kreditkartendaten?",
              "q6_kreditkartendaten",
              "q6_beschreibung",
            ),
            q("Welche Art von Daten speichern Sie (z. B. sensible Kundendaten)?", {
              key: "q7_freitext",
              label: "Beschreibung der gespeicherten Daten",
              type: "textarea",
              placeholder: "Bitte beschreiben …",
            }),
          ],
        },
      ],
    },
    {
      key: "grundfragen",
      title: "Grundfragen zum Cyberschutz",
      color: "pillarOrange",
      subSteps: [
        {
          key: "grundfragen",
          title: "Grundfragen zum Cyberschutz",
          description:
            "Welche grundlegenden Sicherheitsmaßnahmen sind im Unternehmen vorhanden? (Mehrfachauswahl)",
          questions: [
            {
              question: "Vorhandene Schutzmaßnahmen",
              field: {
                key: "grundfragen_auswahl",
                type: "iconCards",
                options: [
                  {
                    value: "mitarbeiterschulungen",
                    label: "Mitarbeiterschulungen",
                    icon: "schulung",
                    color: "#8B6DB0",
                  },
                  {
                    value: "passwortrichtlinie",
                    label: "Passwortrichtlinie",
                    icon: "passwort",
                    color: "#4A90A4",
                  },
                  {
                    value: "antimalware_firewall",
                    label: "Anti-Malware & Firewall",
                    icon: "antimalware",
                    color: "#5A8A5A",
                  },
                  {
                    value: "backup",
                    label: "Wöchentliches Backup",
                    icon: "backup",
                    color: "#E8A020",
                  },
                  {
                    value: "sicherheitspatches",
                    label: "Sicherheitspatches",
                    icon: "patch",
                    color: "#E05020",
                  },
                  {
                    value: "altsysteme_isoliert",
                    label: "Altsysteme isoliert",
                    icon: "altsystem",
                    color: "#475569",
                  },
                ],
              },
            },
          ],
        },
      ],
    },
  ],
};
